import { isSameDay } from 'date-fns';
import {
  useTaskTodayControls,
  useUpdateHabitScheduleEntry,
} from 'features/tasks';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight16, CheckCircle216, Keyboard, X } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenManualEntryLogging } from 'shared/contexts/habit-manual-entry-logging';
import { useToday } from 'shared/contexts/today';
import { Habit } from 'shared/types/habit';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getScheduleFrequencyDayTarget } from 'shared/utils/get-schedule-frequency-day-target';
import { getScheduleByDate } from 'shared/utils/schedules';

export type CompleteListProps = {
  task: Task;
  onClose: () => void;
  date?: Timestamp;
};

export const CompleteList: React.FC<CompleteListProps> = ({
  task,
  onClose,
  date,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const { onComplete } = useTaskTodayControls();
  const updateHabitEntry = useUpdateHabitScheduleEntry();
  const openManualEntryLogging = useOpenManualEntryLogging();

  const completed = useMemo(() => {
    if (task.completedAt) {
      return true;
    }

    if (task.schedules) {
      const dateSchedule = getScheduleByDate(task.schedules, {
        date: date ?? today,
        today,
      });
      const entry = dateSchedule?.entries.find(
        (entry) => entry.date.getTime() === date?.getTime(),
      );
      const frequencyTarget = dateSchedule
        ? getScheduleFrequencyDayTarget(dateSchedule) ??
          dateSchedule.frequency?.count ??
          1
        : 1;
      return (
        entry?.state === ScheduleEntryState.Complete &&
        (dateSchedule?.frequency
          ? (entry.value ?? 0) >= frequencyTarget
          : (entry.value ?? 0) >= (dateSchedule?.completionTarget.count ?? 1))
      );
    }
  }, [date, task.completedAt, task.schedules, today]);

  const _onComplete = () => {
    const dateSchedule = getScheduleByDate(task.schedules, {
      date: date ?? today,
      today,
    });
    const frequencyTarget = dateSchedule
      ? getScheduleFrequencyDayTarget(dateSchedule) ??
        dateSchedule.frequency?.count ??
        1
      : 1;
    onComplete(task, ScheduleEntryState.Complete, {
      referenceDate: date ?? today,
      value: completed
        ? 0
        : dateSchedule
          ? dateSchedule?.frequency
            ? frequencyTarget
            : dateSchedule?.completionTarget.count
          : 1,
    });
    onClose();
  };

  const onSkip = () => {
    updateHabitEntry(task as Habit, date ?? today, ScheduleEntryState.Skip);
    onClose();
  };

  const onFail = () => {
    updateHabitEntry(task as Habit, date ?? today, ScheduleEntryState.Failure);
    onClose();
  };

  const onLog = () => {
    openManualEntryLogging(task.id, date ?? today);
    onClose();
  };

  const isHabit = [TaskType.Habit, TaskType.Repeating].includes(task.type);

  const skippedTodayOrAfter = useMemo(
    () =>
      getActiveSchedule(task.schedules, today)?.entries.find(
        (entry) =>
          entry.state === ScheduleEntryState.Skip &&
          isSameDay(entry.date, today),
      ),
    [task.schedules, today],
  );
  const failedToday = useMemo(
    () =>
      getActiveSchedule(task.schedules, today)?.entries.find(
        (entry) =>
          entry.state === ScheduleEntryState.Failure &&
          isSameDay(entry.date, today),
      ),
    [task.schedules, today],
  );

  return (
    <PopupMenuList hasBottomBorder>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={_onComplete}
          start={<Icon icon={CheckCircle216} />}
        >
          {t(
            task.completedAt
              ? 'context-menus.task.labels.un-complete'
              : 'context-menus.task.labels.complete',
          )}
        </PopupMenuButton>
      </PopupMenuListItem>
      {isHabit && !skippedTodayOrAfter && (
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onSkip}
            start={<Icon icon={ArrowRight16} />}
          >
            {t('context-menus.task.labels.skip')}
          </PopupMenuButton>
        </PopupMenuListItem>
      )}
      {isHabit && !failedToday && (
        <PopupMenuListItem>
          <PopupMenuButton onClick={onFail} start={<Icon icon={X} />}>
            {t('context-menus.task.labels.fail')}
          </PopupMenuButton>
        </PopupMenuListItem>
      )}
      {isHabit && (
        <PopupMenuListItem>
          <PopupMenuButton onClick={onLog} start={<Icon icon={Keyboard} />}>
            {t('context-menus.task.labels.log-progress')}
          </PopupMenuButton>
        </PopupMenuListItem>
      )}
    </PopupMenuList>
  );
};
